/**
 * El cuerpo de letra al que cabe ENTERO lo que se escribe en una casilla.
 *
 * ⚠ **Existe porque la casilla recortaba, y en papel eso es un dato perdido.** Con el
 * cuerpo fijo del formato —10 pt—, «SETIECA AGENTES ADUANALES, C.A.» no cabe en la
 * casilla de la agencia de aduana, y un `input` no avisa: enseña el tramo que cabe y
 * esconde el resto. En pantalla se veía «ECA AGENTES ADUANALES C.A» con el cursor al
 * final; impreso, salía cortado por un lado. Es además el valor que la propia
 * pantalla precarga, así que pasaba casi siempre.
 *
 * ⚠ **Se MIDE con `canvas.measureText`, no se estima por número de letras.** En
 * `componentes/impresos/` hay una estimación —0,78 em por carácter— y sirve allí
 * porque todo es versal de Montserrat Bold. Aquí se escribe en mayúsculas y
 * minúsculas, con cifras, y lo teclea quien sea: una constante por carácter se pasa
 * con «iiiii» y se queda corta con «WWWWW». La medida es de la misma familia con la
 * que se pinta el campo, leída de `--font-sans`, así que no hay dos listas de fuentes
 * que puedan divergir.
 *
 * ⚠ **Baja lo justo y ni un punto más.** El cuerpo de diseño sigue siendo el del
 * formato: sólo se reduce lo que obliga el texto, así que una casilla con un dato
 * corto sale exactamente como en el original.
 */

/**
 * El cuerpo más pequeño al que se deja bajar un campo, en pt.
 *
 * ⚠ **Hay un suelo, y por debajo se AVISA en vez de seguir encogiendo.** Encoger sin
 * límite cumpliría «que se vea completo» con una línea que en papel no se puede leer,
 * que es peor que cortada porque parece que está. 5 pt todavía se lee en una láser;
 * lo que no cabe ni así se marca en rojo en la pantalla —el aviso no se imprime— para
 * que se acorte antes de gastar la hoja.
 */
export const CUERPO_MINIMO = 5

/**
 * Qué fracción del hueco se da por aprovechable.
 *
 * ⚠ **0,96 y no 1.** `canvas` no aplica `tabular-nums`, y las cifras tabulares de los
 * importes son algo más anchas que las que mide; el cursor y el redondeo a píxel del
 * navegador también se comen un poco. Sin este margen, el último carácter asomaba
 * partido por el borde de la casilla.
 */
const APROVECHABLE = 0.96

export type Ajuste = {
  /** El cuerpo al que pintar, en pt. */
  cuerpo: number
  /** Falso si ni al cuerpo mínimo entra entero. */
  cabe: boolean
}

/**
 * La cuenta, sin DOM: dado el ancho del texto a cuerpo 100, qué cuerpo entra.
 *
 * El ancho de un texto es proporcional a su cuerpo, así que basta con medirlo una vez
 * a cuerpo 100 —en cualquier unidad— y escalar: a cuerpo `c` ocupa `ancho × c / 100`
 * de esa misma unidad. Por eso no importa que `canvas` mida en píxeles y la casilla
 * esté en puntos.
 */
export function cuerpoParaAncho(anchoACien: number | null, huecoPt: number, base: number): Ajuste {
  // Sin medida —render en el servidor, o un navegador sin `canvas`— se deja el cuerpo
  // del formato: es lo que se hacía antes, y no inventa un tamaño a ciegas.
  if (anchoACien === null || anchoACien <= 0) return { cuerpo: base, cabe: true }
  const ideal = (100 * huecoPt * APROVECHABLE) / anchoACien
  if (ideal >= base) return { cuerpo: base, cabe: true }
  if (ideal >= CUERPO_MINIMO) return { cuerpo: ideal, cabe: true }
  return { cuerpo: CUERPO_MINIMO, cabe: false }
}

/** El pincel de medir, creado la primera vez que hace falta. `null` si no hay canvas. */
let pincel: CanvasRenderingContext2D | null | undefined
/** La fuente con la que se midió lo que hay en la caché. */
let fuenteMedida = ""
/** Anchos ya medidos, por texto. Se vacía si cambia la fuente. */
const anchos = new Map<string, number>()

function obtenerPincel(): CanvasRenderingContext2D | null {
  if (pincel !== undefined) return pincel
  pincel = null
  try {
    pincel = document.createElement("canvas").getContext("2d")
  } catch {
    // Sin `document` —el render en el servidor— no hay nada que medir.
  }
  return pincel
}

/** El ancho del texto a cuerpo 100, con la fuente de los campos. */
function medir(texto: string): number | null {
  const contexto = obtenerPincel()
  if (contexto === null) return null

  const guardado = anchos.get(texto)
  if (guardado !== undefined) return guardado

  // ⚠ La familia se lee de la hoja de estilos en cada medida NUEVA, no una sola vez al
  // cargar: si la primera medida ocurriera antes de que llegara el CSS, se quedaría
  // midiendo con la fuente genérica toda la sesión.
  const familia = getComputedStyle(document.documentElement).getPropertyValue("--font-sans").trim()
  const fuente = `400 100px ${familia || "sans-serif"}`
  if (fuente !== fuenteMedida) {
    contexto.font = fuente
    fuenteMedida = fuente
    anchos.clear()
  }

  const ancho = contexto.measureText(texto).width
  anchos.set(texto, ancho)
  return ancho
}

/**
 * El cuerpo al que `texto` entra entero en un hueco de `huecoPt` puntos.
 *
 * `base` es el cuerpo del formato original; nunca se sube por encima de él.
 */
export function cuerpoQueEntra(texto: string, huecoPt: number, base: number): Ajuste {
  if (texto === "") return { cuerpo: base, cabe: true }
  return cuerpoParaAncho(medir(texto), huecoPt, base)
}
